import { t } from '../../../lib/i18n';
import { financeErrorMessage } from '../api';

/**
 * The three states every panel on «Фінанси» now has, in one component.
 *
 * ── Why three and not one ───────────────────────────────────────────────────
 * On fixtures this screen COULD NOT FAIL: `fetchFinanceSnapshot` resolved with
 * six clients every time, so there was one spinner for the whole page and no
 * error branch at all. Against real endpoints each panel can be loading, can
 * fail, and — the state that matters most — can be legitimately EMPTY.
 *
 * An empty platform is the NORMAL first-run state. A table that renders a
 * spinner forever is how "no invoices have been issued yet" gets reported as a
 * bug, and an error box where there is simply no data is how it gets escalated
 * as an outage. So empty says empty, in words, and never borrows the spinner.
 *
 * Each state is a sibling of the panel it belongs to rather than a wrapper
 * around the whole screen: a failed journal must leave the balances table
 * standing, because chasing an overdue client does not depend on the ledger.
 */

const BOX =
  'rounded-cf-lg border px-4 py-10 text-center text-[13px] leading-[1.5]';

/** «Завантаження…» — the panel is in flight. */
export function PanelLoading({ label }: { label?: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className={`${BOX} border-cf-line bg-cf-surface text-cf-ink-subtle`}
    >
      {label ?? t('common.loading.data')}
    </p>
  );
}

/**
 * The panel's endpoint refused or fell over.
 *
 * Prefers the API's OWN sentence when a handler sent one — `financeErrorMessage`
 * reads it out of the `{ error: { message, code } }` envelope, because
 * `apiFetch` only lifts `error` into `ApiError.message` when it is a string and
 * on this surface it is an object. A generic "something went wrong" over a
 * server that said exactly what was wrong is a self-inflicted support ticket.
 */
export function PanelError({ error, fallbackKey }: { error: unknown; fallbackKey: string }) {
  const message = financeErrorMessage(error);
  return (
    <p role="alert" className={`${BOX} border-cf-danger bg-cf-danger-tint text-cf-danger`}>
      {message ?? t(fallbackKey)}
    </p>
  );
}

/** The endpoint answered, and the answer is "nothing yet". */
export function PanelEmpty({ messageKey }: { messageKey: string }) {
  return (
    <p className={`${BOX} border-cf-line bg-cf-surface text-cf-ink-subtle`}>{t(messageKey)}</p>
  );
}
